/**
 * @author       yontaek@zumper.com (Yon Taek Chung)
 * @copyright    Copyright (c) 2023, Zumper
 * @description  Reducer for Ratings and Reviews
 */

import RatingsAndReviews, { DEFAULT_MODAL_COUNTS } from '@zcode/nr/es/shared/bizos/reviews'
import { payloadReducer } from '@zcode/reducers'
import type { RatingsAndReviewsModule } from '@zcode/state-type'
import type { Values } from '@zcode/utility-types'
import { combineReducers } from 'redux'
import { type ExtractActions, handleActions } from 'redux-actions'
import type * as ratingsAndReviewsActions from '../actions'
import {
  CLEAR_MODAL_RATING_AND_REVIEWS_COUNTS_DATA,
  CLEAR_MODAL_RATING_AND_REVIEWS_DATA,
  CLEAR_RATING_AND_REVIEWS_DATA,
  RECEIVE_MODAL_RATING_AND_REVIEWS_COUNTS_DATA,
  RECEIVE_MODAL_RATING_AND_REVIEWS_DATA,
  RECEIVE_RATING_AND_REVIEWS_DATA,
} from '../constants/index'
import { metaReducer, modalCountsMetaReducer, modalMetaReducer } from './meta'

type ActionTypes = ExtractActions<Values<typeof ratingsAndReviewsActions>>

export const ratingsAndReviews = combineReducers<RatingsAndReviewsModule, ActionTypes>({
  detail: handleActions(
    {
      [RECEIVE_RATING_AND_REVIEWS_DATA]: payloadReducer,
      [CLEAR_RATING_AND_REVIEWS_DATA]: () => new RatingsAndReviews({ reviews: [], total_count: 0 }),
    },
    new RatingsAndReviews({ reviews: [], total_count: 0 })
  ),
  detailMeta: metaReducer,
  modal: handleActions(
    {
      [RECEIVE_MODAL_RATING_AND_REVIEWS_DATA]: (state, { payload }) => {
        const { shouldAppend } = payload
        delete payload?.shouldAppend

        if (shouldAppend && state?.reviews?.length) {
          return new RatingsAndReviews({
            ...payload._data,
            reviews: [...state._data.reviews, ...payload._data.reviews],
          })
        }

        return new RatingsAndReviews(payload._data)
      },
      [CLEAR_MODAL_RATING_AND_REVIEWS_DATA]: () =>
        new RatingsAndReviews({ reviews: [], total_count: 0 }),
    },
    new RatingsAndReviews({ reviews: [], total_count: 0 })
  ),
  modalMeta: modalMetaReducer,
  modalCounts: handleActions(
    {
      [RECEIVE_MODAL_RATING_AND_REVIEWS_COUNTS_DATA]: (state, action) => {
        const { payload } = action

        return {
          ...DEFAULT_MODAL_COUNTS,
          ...payload,
        }
      },
      [CLEAR_MODAL_RATING_AND_REVIEWS_COUNTS_DATA]: () => DEFAULT_MODAL_COUNTS,
    },
    DEFAULT_MODAL_COUNTS
  ),
  modalCountsMeta: modalCountsMetaReducer,
})
